import ReactEcharts from 'echarts-for-react';
import { useEffect, useRef, useState } from 'react';
import { connect, useDispatch } from 'react-redux';
import { ChartControlsProps } from '../../redux/ChartPoperties/ChartControlsInterface';
import {
  formatChartLabelValueForSelectedMeasure,
  formatChartXAxisValue,
  formatChartYAxisValue,
} from '../ChartOptions/Format/NumberFormatter';
import { ChartsMapStateToProps, ChartsReduxStateProps } from './ChartsCommonInterfaces';
import { useSelector } from 'react-redux';
import { RootState } from '../../redux';

import { palette } from '../..';
import EmptyQuery from './EmptyQuery';
import { updateHoveredItems } from '../../redux/TabTile/TabActions';
import { tooltipStyle } from '../Common/EChartTooltip';
import {
  checkUidPresenceInCheckedMeasureForTooltip,
  limitChartData,
  shouldChartDisplayWithSecurity,
} from '../CommonFunctions/CommonFunctions';
import useCombinedColorSchemes from '../../hooks/useCombinedColorSchemes';
import { messages } from '../CommonFunctions/aliases';
import { toast } from 'react-toastify';
import {
  AXIS_THRESHOLDS,
  LABEL_THRESHOLDS,
  LEGEND_THRESHOLDS,
  MARGIN_THRESHOLDS,
} from '../ChartOptions/ChartThresholds';

const StackedAreaChart = ({
  //props
  propKey,
  graphDimension,
  chartArea,
  graphTileSize,
  colorScheme,
  softUI,
  fontFamily,

  //state
  chartControls,
  chartProperties,
}: ChartsReduxStateProps) => {
  var chartControl: ChartControlsProps = chartControls.properties[propKey];
  const currentControls = chartControl.stackedAreaChartControls || {};
  const { manualColor, nodeValuesColors, allSeriesColor } = currentControls;
  let chartData: any[] = chartControl.chartData ? chartControl.chartData : [];

  const {
    tabState,
    selectedTab,
    showDash,
    tooltipFormats,
    chartAxes,
    isTooltipEnabled,
    chartType,
  } = useSelector((state: RootState) => ({
    tooltipFormats: state.chartControls?.properties[propKey]?.formatOptions?.tooltipFormats,
    chartAxes: state.chartProperties?.properties[propKey]?.chartAxes,
    isTooltipEnabled:
      state.chartControls?.properties[propKey]?.formatOptions?.tooltipFormats?.isEnable,
    tabState: state.tabState,
    selectedTab: state.tabTileProps.selectedTabId,
    showDash: state.tabTileProps.showDash,
    chartType: state.chartProperties?.properties[propKey]?.chartType,
  }));

  const chartRef = useRef<any>(null);
  const chartInstanceRef = useRef<echarts.ECharts | null>(null);
  const dispatch = useDispatch();

  const [seriesData, setSeriesData] = useState<any>([]);

  const [chartDataKeys, setChartDataKeys] = useState<string[]>([]);

  const mouseOverInteractivity =
    tabState.tabs[selectedTab]?.interactivity?.mouseOverInteractivity || false;

  const limitedChartData = limitChartData(chartData, 200, chartAxes[3]?.fields[0]?.displayname);

  const processedChartData = limitedChartData.map((item) => {
    return Object.fromEntries(
      Object.entries(item).map(([key, value]) => [
        key,
        value === null
          ? '(Blank)'
          : typeof value === 'boolean'
            ? value
              ? 'True'
              : 'False'
            : value,
      ]),
    );
  });

  const xAxisName = (processedChartData[0] && Object.keys(processedChartData[0])[0]) || '';

  useEffect(() => {
    if (chartData.length >= 1) {
      if (typeof chartData === 'object' && chartData.length > 0) {
        setChartDataKeys(Object.keys(chartData[0]));
      }
    }
  }, [chartData]);

  useEffect(() => {
    if (chartData.length > 200 && !showDash) {
      toast.warning(`${messages.chart.excessiveData} 200 records`, {
        position: 'top-center',
        autoClose: 10000,
        toastId: 'chart-data-limit-warning',
        style: {
          top: '10px',
        },
      });
    }
  }, [chartData]);

  // useEffect(() => {
  //   var seriesDataTemp = [];
  //   if (chartData.length >= 1) {
  //     var chartDataKeys = Object.keys(chartData[0]);
  //     for (let i = 0; i < Object.keys(chartData[0]).length - 1; i++) {
  //       var seriesObj = {
  //         type: 'line',
  //         smooth: chartControls.properties[propKey].smoothCurve?.enable,
  //         stack: 'All',

  //         lineStyle: softUI
  //           ? {
  //               shadowColor: 'rgba(0, 0, 0, 0.5)', // Shadow color
  //               shadowBlur: 10, // Blur radius
  //               shadowOffsetX: 3, // Horizontal shadow offset
  //               shadowOffsetY: 3,
  //             }
  //           : {},
  //         areaStyle: softUI
  //           ? {
  //               opacity: chartControl.areaOpacity,
  //               shadowColor: 'rgba(0, 0, 0, 0.5)', // Shadow color
  //               shadowBlur: 10, // Blur radius
  //               shadowOffsetX: 3, // Horizontal shadow offset
  //               shadowOffsetY: 3,
  //             }
  //           : {
  //               opacity: chartControl.areaOpacity,
  //             },
  //         label: {
  //           show: chartControl.labelOptions.showLabel,
  //           fontSize: chartControl.labelOptions.fontSize,
  //           color: chartControl.labelOptions.labelColorManual
  //             ? chartControl.labelOptions.labelColor
  //             : selectedTheme?.dark
  //               ? '#ffffff'
  //               : palette.primary.contrastText,

  //           formatter: (value: FormatterValueProps) => {
  //             var formattedValue = value.value[chartDataKeys[i + 1]];
  //             formattedValue = formatChartLabelValueForSelectedMeasure(
  //               chartControls.properties[propKey],
  //               chartProperties.properties[propKey],
  //               formattedValue,
  //               chartDataKeys[i + 1],
  //             );

  //             return formattedValue;
  //           },
  //         },
  //       };
  //       seriesDataTemp.push(seriesObj);
  //     }
  //     setSeriesData(seriesDataTemp);
  //   }
  // }, [chartData, chartControl, softUI]);

  const allThemes = useCombinedColorSchemes();
  const chartThemes = allThemes.filter((el) => {
    if (!chartControl.colorScheme || chartArea === 'dashboard') {
      return el.name === colorScheme;
    } else {
      return el.name === chartControl.colorScheme;
    }
  });
  const selectedTheme = chartThemes.length > 0 ? chartThemes[0] : allThemes[0];

  const getTopMarginForLegend = () => {
    var top = '';
    if (chartControl.legendOptions?.position?.top === 'top') {
      top = 'top';
      // } else if (chartControl.legendOptions?.position?.top === "bottom") {
      //   top = "90%";
    } else {
      top = '50%';
    }
    return top;
  };

  const [hoveredColumns, setHoveredColumns] = useState<Record<string, any>[]>([]);

  useEffect(() => {
    const timeout = setTimeout(() => {
      if (
        !mouseOverInteractivity ||
        (tabState.tabs[selectedTab].hoveredPropkey &&
          tabState.tabs[selectedTab].hoveredPropkey === propKey)
      ) {
        return;
      }
      const chartInstance = chartInstanceRef.current;
      if (!chartInstance || chartInstance.isDisposed?.()) return;

      const index = processedChartData.findIndex((item) =>
        tabState.tabs[selectedTab].hoveredItems?.some(
          (hovered: Record<string, any>) =>
            String(hovered[chartDataKeys[0]]) === String(item[chartDataKeys[0]]),
        ),
      );

      if (index >= 0) {
        for (let i = 0; i < seriesData.length; i++) {
          chartInstance.dispatchAction({
            type: 'highlight',
            seriesIndex: i,
            dataIndex: index,
          });
        }
      } else {
        for (let i = 0; i < seriesData.length; i++) {
          chartInstance.dispatchAction({
            type: 'downplay',
            seriesIndex: i,
          });
        }
      }
    }, 30); // slight delay helps when charts update

    return () => clearTimeout(timeout);
  }, [tabState.tabs[selectedTab].hoveredItems, chartData]);

  const hoverdTimeout = useRef<NodeJS.Timeout | null>(null);
  const hoveredRef = useRef<Record<string, any> | null>(null);

  const handleChartReady = (chartInstance: any) => {
    if (!mouseOverInteractivity) return;

    chartInstanceRef.current = chartInstance;

    chartInstance.on('mouseover', (params: any) => {
      if (params?.name && hoveredRef.current?.[chartDataKeys[0]] !== params.name) {
        if (hoverdTimeout.current) clearTimeout(hoverdTimeout.current);

        hoverdTimeout.current = setTimeout(() => {
          const newEntry = { [chartDataKeys[0]]: params.name };
          hoveredRef.current = newEntry;

          setHoveredColumns((prev) => {
            const isDuplicate = prev.some((item) => item[chartDataKeys[0]] === params.name);
            if (!isDuplicate) {
              return [...prev, newEntry];
            }
            return prev;
          });
        }, 200);
      }
    });

    chartInstance.on('mouseout', (params: any) => {
      if (hoverdTimeout.current) clearTimeout(hoverdTimeout.current);
      if (params?.name) {
        hoveredRef.current = null;
        setHoveredColumns([]);
      }
    });
  };

  const hasSplitBy = chartProperties.properties[propKey].chartAxes[2]?.fields?.length > 0;

  const seriesDataFn = () => {
    if (!processedChartData.length) return [];

    const keys = Object.keys(processedChartData[0]);
    const xKey = keys[0];
    const hasSplitBy = chartProperties.properties[propKey].chartAxes[2]?.fields?.length > 0;

    //  Min/Max settings
    const axisMinMax = chartControl.axisOptions.axisMinMax;

    // clamp values for the visual chart only
    const getClampedValue = (val: any) => {
      if (val === null || val === undefined || isNaN(val)) return val;
      let finalVal = val;

      // Cap at Max
      if (axisMinMax.enableMax && axisMinMax.maxValue !== null && val > axisMinMax.maxValue) {
        finalVal = axisMinMax.maxValue;
      }
      // Floor at Min
      if (axisMinMax.enableMin && axisMinMax.minValue !== null && val < axisMinMax.minValue) {
        finalVal = axisMinMax.minValue;
      }
      return finalVal;
    };

    const buildLabel = (yKey: string) => ({
      show:
        Math.floor(graphDimension.height) > LABEL_THRESHOLDS.common.h
          ? chartControl.labelOptions.showLabel
          : false,
      fontSize: chartControl.labelOptions.fontSize,
      color: chartControl.labelOptions.labelColor
        ? chartControl.labelOptions.labelColor
        : selectedTheme?.dark
          ? '#ffffff'
          : '#757474',
      backgroundColor: chartControl.labelOptions.labelBackground
        ? chartControl.labelOptions.labelBackgroundColor
        : 'transparent',
      borderRadius: chartControl.labelOptions.labelBackground ? 4 : 0,
      padding: chartControl.labelOptions.labelBackground ? [4, 8] : 0,
      formatter: (params: any) => {
        // Use originalValue if available, otherwise fall back to standard value
        const val = params.data?.originalValue ?? params.value?.[1] ?? params.value?.[yKey];
        return formatChartLabelValueForSelectedMeasure(
          chartControls.properties[propKey],
          chartProperties.properties[propKey],
          val,
          yKey,
        );
      },
    });

    // Helper to get color
    const themeColors = selectedTheme?.colors || [];
    const getSeriesColor = (name: string, index: number) => {
      if (manualColor) {
        if (nodeValuesColors && nodeValuesColors[name]) {
          return nodeValuesColors[name];
        }
        if (allSeriesColor) {
          return allSeriesColor;
        }
      }
      return themeColors[index % themeColors.length];
    };

    const baseStyles = (color: string) => ({
      smooth: chartControls.properties[propKey].smoothCurve?.enable,
      stack: 'All', // This makes it stacked
      lineStyle: softUI
        ? {
            color: color,
            shadowColor: 'rgba(0, 0, 0, 0.5)',
            shadowBlur: 10,
            shadowOffsetX: 3,
            shadowOffsetY: 3,
          }
        : { color: color },
      areaStyle: softUI
        ? {
            color: color,
            opacity: chartControl.areaOpacity,
            shadowColor: 'rgba(0, 0, 0, 0.5)',
            shadowBlur: 10,
            shadowOffsetX: 3,
            shadowOffsetY: 3,
          }
        : {
            color: color,
            opacity: chartControl.areaOpacity,
          },
      itemStyle: { color: color },
    });

    let seriesArr: any[] = [];

    if (hasSplitBy) {
      const splitField = chartProperties.properties[propKey].chartAxes[2]?.fields[0]?.displayname;
      const measureField = chartProperties.properties[propKey].chartAxes[3]?.fields[0]?.displayname;

      const groups = [...new Set(processedChartData.map((d) => d[splitField]))];

      seriesArr = groups.map((group, i) => {
        const color = getSeriesColor(group as string, i);
        return {
          name: group,
          type: 'line',
          datasetIndex: 0,
          // Map data: Clamp the plotted value, store original in 'originalValue'
          ...baseStyles(color),
          data: processedChartData
            .filter((d) => d[splitField] === group)
            .map((d) => {
              const rawVal = d[measureField];
              const plottedVal = getClampedValue(rawVal);
              return {
                value: [String(d[xKey]), plottedVal],
                originalValue: rawVal,
              };
            }),
          label: buildLabel(measureField),
        };
      });
    } else {
      const measureFields =
        chartProperties.properties[propKey].chartAxes[3]?.fields.map((f: any) => f.displayname) ||
        [];

      seriesArr = measureFields.map((mKey: string, i: number) => {
        const color = getSeriesColor(mKey, i);
        return {
          name: mKey,
          type: 'line',
          // encode: { x: xKey, y: mKey }, // Removed 'encode' to rely on manual 'data' structure
          datasetIndex: 0,
          // Map data: Clamp the plotted value, store original in 'originalValue'
          ...baseStyles(color),
          data: processedChartData.map((d) => {
            const rawVal = d[mKey];
            const plottedVal = getClampedValue(rawVal);
            return {
              value: [String(d[xKey]), plottedVal],
              originalValue: rawVal,
            };
          }),
          label: buildLabel(mKey),
        };
      });
    }

    return seriesArr;
  };

  useEffect(() => {
    dispatch(updateHoveredItems(selectedTab, hoveredColumns, propKey));
  }, [hoveredColumns]);

  const chartInstance = chartRef.current?.getEchartsInstance?.();
  const currentOption = chartInstance?.getOption();
  const [currentLegendSelected, setCurrentLegendSelected] = useState<any>(
    currentOption?.legend?.[0]?.selected ?? {},
  );

  const onLegendSelectChanged = (params: any) => {
    setCurrentLegendSelected(params.selected);
  };

  const RenderChart = () => {
    return (
      <ReactEcharts
        ref={chartRef}
        onChartReady={handleChartReady}
        onEvents={{
          legendselectchanged: onLegendSelectChanged,
        }}
        // theme={chartControl.colorScheme}
        style={{
          // padding: "1rem",
          width: graphDimension.width,
          height: graphDimension.height,
          overflow: 'hidden',
          margin: 'auto',
          border: chartArea ? 'none' : graphTileSize ? 'none' : '1px solid rgb(238,238,238)',
        }}
        option={{
          color: chartThemes[0]?.colors || [],
          backgroundColor: chartThemes[0]?.background || [],
          // animation: chartArea ? false : true, // Turning animation off for current release, to be added later on
          animation: false,
          legend: {
            // textStyle :{color : getContrastColor(chartThemes[0].background)},
            show:
              Math.floor(graphDimension.height) > LEGEND_THRESHOLDS.stackedArea.h
                ? chartControl.legendOptions?.showLegend
                : false,
            left: chartControl.legendOptions?.position?.left,
            top:
              chartControl.legendOptions?.position?.top !== 'bottom'
                ? getTopMarginForLegend()
                : null,
            // top: chartControl.legendOptions?.position?.top,
            bottom: chartControl.legendOptions?.position?.top === 'bottom' ? 0 : null,
            orient: chartControl.legendOptions?.orientation,
            itemHeight: chartControl.legendOptions?.symbolHeight,
            itemWidth: chartControl.legendOptions?.symbolWidth,
            itemGap: chartControl.legendOptions?.itemGap,
            selected: currentLegendSelected,
            textStyle: {
              color: selectedTheme?.dark ? '#ffffff' : palette.primary.contrastText,
              fontFamily: fontFamily,
            },
          },
          tooltip: {
            show: isTooltipEnabled,
            useHTML: true,
            trigger: 'item',
            backgroundColor: 'white',
            borderWidth: 1,
            padding: 0,
            textStyle: {
              color: '#333',
              fontSize: 16,
            },
            appendToBody: true,
            extraCssText:
              'box-shadow: 0 2px 8px rgba(0,0,0,0.15);white-space: normal; word-break: break-word;',
            formatter: function (params: any) {
              //----select measure field which is hovered and dimension field(s)----//
              const hasSplitBy =
                chartProperties.properties[propKey].chartAxes[2]?.fields?.length > 0;
              let row;

              if (hasSplitBy) {
                // For split by charts, find the row based on x-axis value and series name
                const xValue = params.name || params.value?.[0];
                const splitFieldName =
                  chartProperties.properties[propKey].chartAxes[2]?.fields[0]?.displayname;
                row = processedChartData.find(
                  (item) =>
                    String(item[Object.keys(processedChartData[0])[0]]) === String(xValue) &&
                    String(item[splitFieldName]) === String(params.seriesName),
                );
              } else {
                row = processedChartData[params.dataIndex];
              }

              if (!row) return '';

              let hoveredFieldname, tempData;
              const dimensionAxis = chartAxes[1];
              const dimensionAxisDisplayNames = dimensionAxis.fields.map(
                (field: any) => field.displayname,
              );

              if (hasSplitBy) {
                // For split-by charts, use the measure field name and include split field
                const measureField =
                  chartProperties.properties[propKey].chartAxes[3]?.fields[0]?.displayname;
                const splitField =
                  chartProperties.properties[propKey].chartAxes[2]?.fields[0]?.displayname;
                hoveredFieldname = measureField;

                tempData = {
                  ...Object.fromEntries(
                    dimensionAxisDisplayNames.map((key: any) => [key, row[key]]),
                  ),
                  [splitField]: row[splitField],
                  [measureField]: row[measureField],
                };
              } else {
                // For non-split charts, use series name as hovered field
                hoveredFieldname = params.seriesName;
                const allFieldName = [...dimensionAxisDisplayNames, hoveredFieldname];
                const newData: Record<string, any> = {};
                allFieldName.map((key: string) => (newData[key] = row[key]));
                tempData = newData;
              }

              //---------get list of non hovered field -------//
              const allFieldName = hasSplitBy
                ? [
                    ...dimensionAxisDisplayNames,
                    hoveredFieldname,
                    chartProperties.properties[propKey].chartAxes[2]?.fields[0]?.displayname,
                  ]
                : [...dimensionAxisDisplayNames, hoveredFieldname];

              const nonHoveredFieldList = [
                ...new Set(
                  processedChartData
                    .map((data: any) =>
                      Object.keys(data).filter((key: string) => !allFieldName.includes(key)),
                    )
                    .flat(),
                ),
              ];
              //----------remove unchecked measure or any other field value of type date inside tooltip----------//
              const _data: { [key: string]: string | number } = {};
              Object.keys(tempData).forEach((key: string) => {
                const resp = checkUidPresenceInCheckedMeasureForTooltip(
                  chartAxes,
                  key,
                  tooltipFormats?.checkedMeasure,
                );
                if (resp) {
                  _data[key] = tempData[key];
                }
              });
              let mergedData = { ..._data };
              //-- send extra responses in _data so that it maps and shows all key value pairs including extra measure (if any)--//
              //----get extra response corresponding to all dimensions and create tooltip data----//
              let extendedDimensionNames = dimensionAxisDisplayNames;
              if (hasSplitBy) {
                const splitField =
                  chartProperties.properties[propKey].chartAxes[2]?.fields[0]?.displayname;
                extendedDimensionNames = [...dimensionAxisDisplayNames, splitField];
              }
              const extraResponses = Array.isArray(tooltipFormats?.extraMeasureFieldsResponses)
                ? tooltipFormats.extraMeasureFieldsResponses
                : [];
              if (extraResponses.length > 0) {
                const lookupKeys = extendedDimensionNames;
                const tooltipData = extraResponses.find((obj: any) =>
                  lookupKeys.every((dimensionKey: string) => {
                    const dimensionValue = tempData[dimensionKey as string];
                    return (
                      dimensionValue !== undefined &&
                      String(obj[dimensionKey as string]) === String(dimensionValue as string)
                    );
                  }),
                );

                let filteredFields = tooltipData ? { ...tooltipData } : {};
                if (filteredFields) {
                  lookupKeys.forEach((dimensionKey: string) => {
                    delete filteredFields[dimensionKey];
                  });
                }

                if (tooltipData && Object.keys(filteredFields).length > 0) {
                  const isHoveredFieldChecked = checkUidPresenceInCheckedMeasureForTooltip(
                    chartAxes,
                    hoveredFieldname,
                    tooltipFormats?.checkedMeasure,
                  );

                  if (!isHoveredFieldChecked) {
                    delete filteredFields[hoveredFieldname];
                  }

                  Object.keys(filteredFields).forEach((key) => {
                    if (
                      nonHoveredFieldList.includes(key) ||
                      !checkUidPresenceInCheckedMeasureForTooltip(
                        chartAxes,
                        key,
                        tooltipFormats?.checkedMeasure,
                      )
                    ) {
                      delete filteredFields[key];
                    }
                  });

                  mergedData = { ...mergedData, ...filteredFields };
                }
              }

              return tooltipStyle(
                params.color,
                mergedData,
                tooltipFormats,
                chartAxes,
                extendedDimensionNames,
                undefined,
                undefined,
                fontFamily,
              );
            },
          },
          grid: {
            left:
              Math.floor(graphDimension.height) <= MARGIN_THRESHOLDS.compact.h
                ? 5
                : chartControl.chartMargin.left + 5 + '%',
            right:
              Math.floor(graphDimension.height) <= MARGIN_THRESHOLDS.compact.h
                ? 5
                : chartControl.chartMargin.right + '%',
            top:
              Math.floor(graphDimension.height) <= MARGIN_THRESHOLDS.compact.h
                ? 9
                : chartControl.legendOptions?.position?.top === 'top'
                  ? chartControl.chartMargin.top + 5 + '%'
                  : chartControl.chartMargin.top + '%',
            bottom:
              Math.floor(graphDimension.height) <= MARGIN_THRESHOLDS.compact.h
                ? 5
                : chartControl.legendOptions?.position?.top === 'bottom'
                  ? (graphDimension.height * chartControl.chartMargin.bottom) / 100 + 35
                  : chartControl.chartMargin.bottom + '%',
            shadowColor: 'rgba(0, 0, 0, 0.3)',
            shadowBlur: 5,
            shadowOffsetX: 2,
            shadowOffsetY: 2,
          },
          dataset: {
            // dimensions: Object.keys(processedChartData[0]),
            source: processedChartData,
          },
          xAxis: {
            type: 'category',
            boundaryGap:
              Math.floor(graphDimension.height) > AXIS_THRESHOLDS.common.h ? true : false,
            position: chartControl.axisOptions.xAxis[1].position,

            axisLine: {
              show:
                chartControl.axisOptions.xAxis[1].position !== 'hide' &&
                Math.floor(graphDimension.height) > AXIS_THRESHOLDS.common.h,
              onZero:
                chartControl.axisOptions.xAxis[1].position === 'top'
                  ? false
                  : chartControl.axisOptions.xAxis[1].onZero,
            },

            axisTick: {
              show:
                chartControl.axisOptions.xAxis[1].position !== 'hide' &&
                Math.floor(graphDimension.height) > AXIS_THRESHOLDS.common.h,
              alignWithLabel: true,
              length:
                chartControl.axisOptions.xAxis[1].position === 'top'
                  ? chartControl.axisOptions.xAxis[1].tickSizeTop
                  : chartControl.axisOptions.xAxis[1].tickSizeBottom,
            },
            axisLabel: {
              show:
                chartControl.axisOptions.xAxis[1].position !== 'hide' &&
                Math.floor(graphDimension.height) > AXIS_THRESHOLDS.common.h,
              fontSize: chartControl.axisOptions.xAxis[1].fontSize,
              rotate:
                chartControl.axisOptions.xAxis[1].position === 'top'
                  ? chartControl.axisOptions.xAxis[1].tickRotationTop
                  : chartControl.axisOptions.xAxis[1].tickRotationBottom,
              margin:
                chartControl.axisOptions.xAxis[1].position === 'top'
                  ? chartControl.axisOptions.xAxis[1].tickPaddingTop
                  : chartControl.axisOptions.xAxis[1].tickPaddingBottom,
              color: selectedTheme?.dark ? '#ffffff' : palette.primary.contrastText,
              fontFamily: fontFamily,
              formatter: (value: any) => {
                const fieldInAxes = chartAxes
                  ?.flatMap((axis: any) => axis?.fields || [])
                  ?.find((f: any) => f.displayname === xAxisName);

                const measureUid = fieldInAxes?.uId;

                const labelFormat = chartControl?.formatOptions?.labelFormats?.measureFormats?.[
                  measureUid
                ] as any;

                if (labelFormat?.finalDateFormat) {
                  return formatChartXAxisValue(chartControl, value, labelFormat);
                }

                return value;
              },
            },
            splitLine: {
              show: chartControl.axisOptions.xSplitLine ?? false,
              lineStyle: {
                color: chartControl.axisOptions.xAxis[1]?.splitLineColor || '#d3d3d3',
                width: chartControl.axisOptions.xAxis[1]?.splitLineWidth ?? 1,
                type: chartControl.axisOptions.xAxis[1]?.splitLineType || 'solid',
              },
            },
            show: true,

            name:
              Math.floor(graphDimension.height) > AXIS_THRESHOLDS.common.h
                ? chartControl.axisOptions.xAxis[1].name
                : '',
            nameLocation: chartControl.axisOptions.xAxis[1].nameLocation,
            nameGap: chartControl.axisOptions.xAxis[1].nameGap,
            nameTextStyle: {
              fontSize: chartControl.axisOptions.xAxis[1].nameSize,
              color: chartControl.axisOptions.xAxis[1].nameColor,
            },
          },
          yAxis: {
            inverse: chartControl.axisOptions.inverse,

            position: chartControl.axisOptions.yAxis[1].position,

            axisLine: {
              show:
                chartControl.axisOptions.yAxis[1].position !== 'hide' &&
                Math.floor(graphDimension.height) > AXIS_THRESHOLDS.common.h,
              onZero: chartControl.axisOptions.yAxis[1].onZero,
            },

            min: chartControl.axisOptions.axisMinMax.enableMin
              ? chartControl.axisOptions.axisMinMax.minValue
              : null,
            max: chartControl.axisOptions.axisMinMax.enableMax
              ? chartControl.axisOptions.axisMinMax.maxValue
              : null,

            axisTick: {
              show:
                chartControl.axisOptions.yAxis[1].position !== 'hide' &&
                Math.floor(graphDimension.height) > AXIS_THRESHOLDS.common.h,
              alignWithLabel: true,
              length:
                chartControl.axisOptions.yAxis[1].position === 'left'
                  ? chartControl.axisOptions.yAxis[1].tickSizeLeft
                  : chartControl.axisOptions.yAxis[1].tickSizeRight,
            },

            axisLabel: {
              show:
                chartControl.axisOptions.yAxis[1].position !== 'hide' &&
                Math.floor(graphDimension.height) > AXIS_THRESHOLDS.common.h,
              fontSize: chartControl.axisOptions.yAxis[1].fontSize,
              rotate:
                chartControl.axisOptions.yAxis[1].position === 'left'
                  ? chartControl.axisOptions.yAxis[1].tickRotationLeft
                  : chartControl.axisOptions.yAxis[1].tickRotationRight,
              margin:
                chartControl.axisOptions.yAxis[1].position === 'left'
                  ? chartControl.axisOptions.yAxis[1].tickPaddingLeft
                  : chartControl.axisOptions.yAxis[1].tickPaddingRight,
              color: selectedTheme?.dark ? '#ffffff' : palette.primary.contrastText,
              fontFamily: fontFamily,
              formatter: (value: number) => {
                var formattedValue = formatChartYAxisValue(chartControl, value);
                return formattedValue;
              },
            },
            splitLine: {
              show: chartControl.axisOptions.ySplitLine ?? true,
              lineStyle: {
                color: chartControl.axisOptions.yAxis[1]?.splitLineColor || '#d3d3d3',
                width: chartControl.axisOptions.yAxis[1]?.splitLineWidth ?? 1,
                type: chartControl.axisOptions.yAxis[1]?.splitLineType || 'solid',
              },
            },

            show: true,
            name:
              Math.floor(graphDimension.height) > AXIS_THRESHOLDS.common.h
                ? chartControl.axisOptions.yAxis[1].name
                : '',
            nameLocation: chartControl.axisOptions.yAxis[1].nameLocation,
            nameGap: chartControl.axisOptions.yAxis[1].nameGap,
            nameTextStyle: {
              fontSize: chartControl.axisOptions.yAxis[1].nameSize,
              color: chartControl.axisOptions.yAxis[1].nameColor,
            },
          },
          series: seriesDataFn(),
        }}
      />
    );
  };

  const emptyQueryCondition =
    shouldChartDisplayWithSecurity(chartAxes, chartType, chartData) === false ||
    chartData.length < 1;

  return emptyQueryCondition ? (
    <EmptyQuery
      graphDimension={graphDimension}
      propKey={propKey}
      chartArea={chartArea}
      graphTileSize={graphTileSize}
      chartThemes={chartThemes}
      showEmptyQueryMessageWithSecurity={
        !shouldChartDisplayWithSecurity(chartAxes, chartType, chartData) ? true : false
      }
    />
  ) : (
    <RenderChart />
  );
};
const mapStateToProps = (state: ChartsMapStateToProps, ownProps: any) => {
  return {
    chartControls: state.chartControls,
    chartProperties: state.chartProperties,
  };
};

export default connect(mapStateToProps, null)(StackedAreaChart);
